import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

type Step = 'idle' | 'qty' | 'price' | 'done';

@Component({
  selector: 'app-how-it-works-tutorial',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './how-it-works-tutorial.component.html',
  styleUrls: ['./how-it-works-tutorial.component.css'],
})
export class HowItWorksTutorialComponent implements AfterViewInit, OnDestroy {
  @ViewChild('stage', { static: true }) stageRef!: ElementRef<HTMLDivElement>;
  @ViewChild('qty', { static: true }) qtyRef!: ElementRef<HTMLInputElement>;
  @ViewChild('price', { static: true }) priceRef!: ElementRef<HTMLInputElement>;
  @ViewChild('viewport', { static: true }) viewportRef!: ElementRef<HTMLDivElement>;


  step: Step = 'idle';
  qtyValue = '';
  priceValue = '';

  // Cursor position in stage coordinates
  cursorX = 40;
  cursorY = 40;
  cursorDown = false;

  private timers: number[] = [];
  private abort = false;

  ngAfterViewInit(): void {
    // Start a little later to avoid layout shifts
    this.timers.push(window.setTimeout(() => this.loop(), 3500));
  }

  ngOnDestroy(): void {
    this.abort = true;
    this.timers.forEach((t) => window.clearTimeout(t));
  }

  private loop() {
    if (this.abort) return;

    this.reset();
    this.timers.push(
      window.setTimeout(async () => {
        await this.moveTo(this.qtyRef.nativeElement);
        this.step = 'qty';
        await this.typeInto('qty', '10', 110);

        await this.moveTo(this.priceRef.nativeElement);
        this.step = 'price';
        await this.typeInto('price', '2.00 €', 95);

        this.step = 'done';

        // Pause at the end, then restart
        this.timers.push(window.setTimeout(() => this.loop(), 6100));
      }, 300)
    );
  }

  private reset() {
    this.step = 'idle';
    this.qtyValue = '';
    this.priceValue = '';
    this.cursorDown = false;

    // Place cursor near first row on reset
    this.cursorX = 70;
    this.cursorY = 150;

    // Clear focus
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  private async moveTo(targetEl: HTMLElement, duration = 520) {

    await this.ensureVisibleInSheet(targetEl);
    const stage = this.stageRef.nativeElement;
    const stageRect = stage.getBoundingClientRect();
    const r = targetEl.getBoundingClientRect();

    // target point: a bit inside the input (left-middle)
    const tx = r.left - stageRect.left + 18;
    const ty = r.top - stageRect.top + r.height / 2;

    await this.tweenCursor(tx, ty, duration);
  }

  private async ensureVisibleInSheet(targetEl: HTMLElement) {
    const viewport = this.viewportRef.nativeElement;

    const vRect = viewport.getBoundingClientRect();
    const tRect = targetEl.getBoundingClientRect();

    // distance of target from viewport content start
    const currentLeft = viewport.scrollLeft;

    // If target is left of viewport or right of viewport -> scroll horizontally
    const targetLeft = (tRect.left - vRect.left) + currentLeft;
    const targetRight = targetLeft + tRect.width;

    let nextLeft = currentLeft;

    const padding = 40; // keep a little breathing room

    if (targetLeft < currentLeft + padding) {
      nextLeft = Math.max(0, targetLeft - padding);
    } else if (targetRight > currentLeft + viewport.clientWidth - padding) {
      nextLeft = Math.max(0, targetRight - viewport.clientWidth + padding);
    }

    if (nextLeft !== currentLeft) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      viewport.scrollTo({ left: nextLeft, behavior: reduce ? 'auto' : 'smooth' });
      await this.wait(350); // wait for smooth scroll to settle
    }
  }


  private async typeInto(which: 'qty' | 'price', text: string, delay = 90) {
    // Clear first
    if (which === 'qty') this.qtyValue = '';
    if (which === 'price') this.priceValue = '';

    for (const ch of text.split('')) {
      if (this.abort) return;
      if (which === 'qty') this.qtyValue += ch;
      if (which === 'price') this.priceValue += ch;
      await this.wait(delay);
    }
  }

  private tweenCursor(tx: number, ty: number, duration: number) {
    const sx = this.cursorX;
    const sy = this.cursorY;
    const start = performance.now();

    return new Promise<void>((resolve) => {
      const step = (now: number) => {
        if (this.abort) return resolve();
        const t = Math.min(1, (now - start) / duration);
        // easeOutCubic
        const e = 1 - Math.pow(1 - t, 3);

        this.cursorX = sx + (tx - sx) * e;
        this.cursorY = sy + (ty - sy) * e;

        if (t < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });
  }

  private wait(ms: number) {
    return new Promise<void>((resolve) => {
      this.timers.push(window.setTimeout(() => resolve(), ms));
    });
  }

  get total(): string {
    const qty = parseInt(this.qtyValue || '0', 10);
    const price = parseFloat(
      (this.priceValue || '0').replace('€', '').replace(',', '.').trim()
    );
    const val = qty * (isNaN(price) ? 0 : price);
    if (!val) return '—';
    // Keep euro formatting simple and stable
    return `${val.toFixed(2)} €`;
  }
}
